import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, DialogContentText, Divider, Grid, TableCell } from '@mui/material';
import moment from 'moment';
import { DialogDefaultV2, InputV2, InputFormatV2, TableWidgetV2 } from '../../../components/v2';
import * as selectorsControlePontos from '../../../store/controlePonto/reducer';
import * as actionsControlePontos from '../../../store/controlePonto/actions';

const toNumeric = (value) => (value != null ? Number(value) : 0);

const parseToMinutes = (hora) => {
  if (!hora) return 0;
  const isNegative = hora.includes('-');
  let value = hora.replace('-', '');
  let minutes = 0;

  if (value.includes(':')) {
    const splited = value.split(':');
    minutes = toNumeric(splited[0]) * 60 + toNumeric(splited[1] || 0);
  } else {
    minutes = toNumeric(value) * 60;
  }

  return isNegative ? minutes * -1 : minutes;
};

const format = (minutes) => {
  let isNegative = false;
  if (minutes < 0) {
    isNegative = true;
    minutes = minutes * -1;
  }
  const hoursValue = Math.floor(minutes / 60);
  const minutesValue = minutes % 60;
  return `${isNegative ? '-' : ''}${String(hoursValue).padStart(2, '0')}:${String(minutesValue).padStart(2, '0')}`;
};

const calculateHoraExtra = (ponto) => {
  const horasCumpridas = parseToMinutes(ponto.horasCumpridas);
  const horasEsperadas = parseToMinutes(ponto.horasEsperadas);
  const horasPagas = parseToMinutes(ponto.horasPagas);
  const horasDescontadas = parseToMinutes(ponto.horasDescontadas);
  return format(horasCumpridas - horasEsperadas + horasPagas - horasDescontadas);
};

const calculateCurrentSaldo = (currentSaldo, horaExtra) =>
  format(parseToMinutes(currentSaldo) + parseToMinutes(horaExtra));

const calculateSaldoTotal = (saldoOutrosAnos, currentSaldo, horaExtra) =>
  format(parseToMinutes(saldoOutrosAnos) + parseToMinutes(currentSaldo) + parseToMinutes(horaExtra));

const Formulario = ({ open, id, onClose, loadControlePontosByUsuarioId, controlePonto, controlePontoUsuarios, saveControlePontos, reportControlePontoByUsuarioId }) => {
  const [filter, setFilter] = useState({ year: moment().format('YYYY') });
  const [values, setValues] = useState({ usuarioId: '', usuario: '', pontos: [], saldo: 0, saldoOutrosAnos: 0 });
  const [openReport, setOpenReport] = useState(false);

  useEffect(() => {
    if (id && controlePontoUsuarios && controlePontoUsuarios.length > 0) {
      const u = controlePontoUsuarios.find((u) => u.id === id);
      if (u) {
        setValues({ usuarioId: id, usuario: u.nome, saldo: u.saldo || 0, pontos: [], saldoOutrosAnos: 0 });
      }
    }
  }, [controlePontoUsuarios, id]);

  useEffect(() => {
    if (controlePonto && controlePonto.pontos && controlePonto.pontos.length > 0) {
      setValues({
        usuarioId: id,
        usuario: controlePonto.usuario,
        pontos: controlePonto.pontos,
        saldo: controlePonto.saldo,
        saldoOutrosAnos: controlePonto.saldoOutrosAnos,
      });
    }
  }, [controlePonto]);

  const onSave = () => saveControlePontos(values, () => onClose());

  const onChange = (rowId, field, value) => {
    const ponto = values.pontos.find((p) => p.id === rowId);
    if (!ponto) return;

    const updated = { ...ponto, [field]: value };
    updated.horaExtra = calculateHoraExtra(updated);

    let currentSaldo = '00:00';
    values.pontos.filter((p) => p.id !== rowId).forEach((p) => {
      currentSaldo = calculateCurrentSaldo(currentSaldo, p.horaExtra);
    });

    setValues({
      ...values,
      pontos: [...values.pontos.filter((p) => p.id !== rowId), updated].sort((a, b) => new Date(a.dia) - new Date(b.dia)),
      saldo: calculateSaldoTotal(values.saldoOutrosAnos, currentSaldo, updated.horaExtra),
    });
  };

  const renderRows = (r) => (
    <>
      <TableCell size="small">{r.mes}</TableCell>
      <TableCell size="small">
        <InputV2 value={r.horasCumpridas} onChange={(e) => onChange(r.id, 'horasCumpridas', e.target.value)} placeholder="00:00" />
      </TableCell>
      <TableCell size="small">
        <InputV2 value={r.horasEsperadas} onChange={(e) => onChange(r.id, 'horasEsperadas', e.target.value)} placeholder="00:00" />
      </TableCell>
      <TableCell size="small">
        <InputV2 value={r.horasPagas} onChange={(e) => onChange(r.id, 'horasPagas', e.target.value)} placeholder="00:00" />
      </TableCell>
      <TableCell size="small">
        <InputV2 value={r.horasDescontadas} onChange={(e) => onChange(r.id, 'horasDescontadas', e.target.value)} placeholder="00:00" />
      </TableCell>
      <TableCell size="small">{r.horaExtra}</TableCell>
    </>
  );

  return (
    <>
      <DialogDefaultV2
        open={open}
        handlerCancel={onClose}
        handlerConfirm={onSave}
        title="Folha de Ponto"
        confirmMessage="Salvar"
        maxWidth="md"
        headerAction={[{ title: 'Relatório', onClick: () => setOpenReport(true) }]}
      >
        <Grid container spacing={3}>
          <Grid size={{ xs: 6 }}>
            <InputV2 label="Usuário" disabled value={values.usuario} />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <InputV2 label="Saldo" disabled value={values.saldo} />
          </Grid>
          <Grid size={{ xs: 4 }}>
            <InputFormatV2
              label="Ano"
              value={filter.year}
              onChange={(e) => setFilter({ year: e.target.value })}
              format="####"
              placeholder="AAAA"
            />
          </Grid>
          <Grid size={{ xs: 4 }} sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              color="primary"
              size="small"
              variant="contained"
              onClick={() => loadControlePontosByUsuarioId(id, filter)}
            >
              Buscar
            </Button>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Divider />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TableWidgetV2
              rows={values.pontos}
              titles={['Mês', 'Qtd. Horas Cumpridas', 'Qtd. Horas Esperadas', 'Horas Pagas', 'Horas Descontadas', 'Hora Extra']}
              headerTitle="Saldos"
              renderRows={renderRows}
              hasAdd={false}
              showDelete={false}
              showEdit={false}
              size="small"
              defaultRowsPerPage={12}
            />
          </Grid>
        </Grid>
      </DialogDefaultV2>
      <DialogDefaultV2
        open={openReport}
        handlerCancel={() => setOpenReport(false)}
        handlerConfirm={() => reportControlePontoByUsuarioId(id, filter, () => setOpenReport(false))}
        title="Confirmação"
        confirmMessage="Gerar"
      >
        <DialogContentText>Deseja gerar o relatório para o usuário?</DialogContentText>
      </DialogDefaultV2>
    </>
  );
};

const mapStateToProps = (state) => ({
  controlePontoUsuarios: selectorsControlePontos.getControlePontosUsuarios(state),
  controlePonto: selectorsControlePontos.getSelectedControlePonto(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({ ...actionsControlePontos }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Formulario);
